import {find} from 'lodash';
import createReducer from '../../common/util/createReducer';
import {ActionTypes} from '../actions';
import {Session, InvoiceLoad} from '../../common/actionTypes';
import {TOYOTA_RUC} from '../../common/constants/dealer';
import {downloadFile} from '../../common/util/file';

const FORM_INITIAL_STATE = {
  'loading': false,
  'savingItem': false,
  'user': '',
  'values': {
    'billClient': TOYOTA_RUC.toString(),
    'isReprocessing': 'no',
    'requiresInspection': 'yes',
    'responsible': '',
    'workStartDate': '',
    'deliveryDate': '',
    'observations': '',
    'status': '',
    'parts': [],
    'tasks': [],
  },
};

const updateDefaultResponsible = (user, techniciansList) => {
  const defaultTechnician = find(techniciansList, {'relatedUser': user.toUpperCase()});
  return defaultTechnician ? defaultTechnician.technician : '';
};

function getItemsSaved(list) {
  return list.map((item) => {
    const newItem = {...item};
    newItem.itemSaved = true;

    const total = item.quantity * item.unitPrice;
    newItem.totalPrice = parseFloat(total.toFixed(2));
    return newItem;
  });
}

function getPartWithStockUpdated(list, {index, partInfo}) {
  const newList = [...list];
  const newObject = {...newList[index]};
  newObject.partName = partInfo.partName;
  newObject.availableStock = partInfo.availableStockInWarehouse;
  newObject.quantity = null;
  newObject.unitPrice = partInfo.unitPrice;

  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
}

function getPartWithTotalsUpdated(list, {index, quantity, unitPrice}) {
  const newList = [...list];
  const newObject = {...newList[index]};

  if (quantity) {
    newObject.quantity = parseInt(quantity, 10);
  }
  if (unitPrice) {
    newObject.unitPrice = parseFloat(unitPrice);
  }
  if (newObject.quantity && newObject.unitPrice) {
    const total = newObject.quantity * newObject.unitPrice;
    newObject.totalPrice = parseFloat(total.toFixed(2));
  }
  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
}

function saveOrDeleteItem(list, {index, action}) {
  const newList = [...list];
  if (action === 'add') {
    const newObject = {...newList[index]};
    newObject.itemSaved = true;
    return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
  }
  
  newList.splice(index, 1);
  return newList;
}

function getTaskWithInfoUpdated(list, {index, taskInfo}) {
  const newObject = {...taskInfo};
  if (list[index].serviceCode3 === newObject.serviceCode3) {
    return list;
  }
  newObject.quantity = null;
  newObject.unitPrice = taskInfo.flatRatePrice;
  newObject.totalPrice = null;
  newObject.outsourced = null;
  newObject.technician = null;

  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
}


function getTasksWithTotalsUpdated(list, {index, quantity, unitPrice}) {
  const newList = [...list];
  const newObject = {...newList[index]};

  if (quantity) {
    newObject.quantity = parseInt(quantity, 10);
  }
  if (unitPrice) {
    newObject.unitPrice = parseFloat(unitPrice);
  }
  if (newObject.quantity && newObject.unitPrice) {
    const total = newObject.quantity * newObject.unitPrice;
    newObject.totalPrice = parseFloat(total.toFixed(2));
  }
  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
}

function addOutsourcedInfo(list, {index, outsourcedInfo}) {
  const newList = [...list];
  const newObject = {...newList[index], ...outsourcedInfo};

  const total = newObject.quantity * newObject.unitPrice;
  newObject.totalPrice = parseFloat(total.toFixed(2));

  return [...list.slice(0, index), newObject, ...list.slice(index + 1)];
}

export const qualityAssuranceOrdersForm = (state, action) => {
  return createReducer(FORM_INITIAL_STATE, {
    [Session.USER_LOGIN_SUCCESS]: (state, action) => ({
      ...state,
      'user': action.payload.tokenParsed.preferred_username.toUpperCase(),
    }),
    [ActionTypes.FETCHED_TECHNICIANS]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'responsible': updateDefaultResponsible(state.user, action.response.data),
      },
    }),
    [ActionTypes.FETCHED_TECHNICIANS_FAILED]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'responsible': '',
      },
    }),
    [ActionTypes.FETCHING_PARTS_FOR_QUALITY_ASSURANCE_ORDER]: (state, action) => {
      return {
        ...state,
        'loading': true,
      };
    },
    [ActionTypes.FETCHED_PARTS_FOR_QUALITY_ASSURANCE_ORDER]: (state, action) => {
      return {
        ...state,
        'loading': false,
        'values': {
          ...state.values,
          'parts': getItemsSaved(action.response.data),
        },
      };
    },
    [ActionTypes.FETCHED_PARTS_FOR_QUALITY_ASSURANCE_ORDER_FAILED]: (state, action) => {
      return {
        ...state,
        'loading': false,
        'values': {
          ...state.values,
          'parts': [],
        },
      };
    },
    [ActionTypes.FETCHED_QUALITY_ASSURANCE_ORDER]: (state, action) => {
      return {
        ...state,
        'values': {
          ...state.values,
          'status': action.response.data.status,
        },
      };
    },
    [ActionTypes.FETCHED_QUALITY_ASSURANCE_ORDER_FAILED]: (state, action) => {
      return {
        ...state,
        'values': {
          ...state.values,
          'status': '',
        },
      };
    },
    [ActionTypes.ADD_PART_INFO]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'parts': getPartWithStockUpdated(state.values.parts, action.payload),
      },
    }),
    [InvoiceLoad.UPDATE_TOTALS]: (state, action) => {
      if (action.payload.type === 'part') {
        return ({
          ...state,
          'values': {
            ...state.values,
            'parts': getPartWithTotalsUpdated(state.values.parts, action.payload),
          },
        });
      }
      return ({
        ...state,
        'values': {
          ...state.values,
          'tasks': getTasksWithTotalsUpdated(state.values.tasks, action.payload),
        },
      });
    },
    [ActionTypes.MANAGING_PART_IN_QUALITY_ASSURANCE_ORDER]: (state, action) => ({
      ...state,
      'savingItem': true,
    }),
    [ActionTypes.MANAGED_PART_IN_QUALITY_ASSURANCE_ORDER]: (state, action) => ({
      ...state,
      'savingItem': false,
      'values': {
        ...state.values,
        'parts': saveOrDeleteItem(state.values.parts, action.payload),
      },
    }),
    [ActionTypes.MANAGED_PART_IN_QUALITY_ASSURANCE_ORDER_FAILED]: (state, action) => ({
      ...state,
      'savingItem': false,
    }),
    [ActionTypes.FETCHING_TASKS_BY_QUALITY_ASSURANCE_ORDER]: (state, action) => {
      return {
        ...state,
        'loading': true,
      };
    },
    [ActionTypes.FETCHED_TASKS_BY_QUALITY_ASSURANCE_ORDER]: (state, action) => {
      return {
        ...state,
        'loading': false,
        'values': {
          ...state.values,
          'tasks': getItemsSaved(action.response.data),
        },
      };
    },
    [ActionTypes.FETCHED_TASKS_BY_QUALITY_ASSURANCE_ORDER_FAILED]: (state, action) => {
      return {
        ...state,
        'loading': false,
        'values': {
          ...state.values,
          'tasks': [],
        },
      };
    },
    [ActionTypes.ADD_TASK_INFO]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'tasks': getTaskWithInfoUpdated(state.values.tasks, action.payload),
      },
    }),
    [ActionTypes.MANAGING_TASK_IN_QUALITY_ASSURANCE_ORDER]: (state, action) => ({
      ...state,
      'savingItem': true,
    }),
    [ActionTypes.MANAGED_TASK_IN_QUALITY_ASSURANCE_ORDER]: (state, action) => {
      const {outsourced, serviceCode3, order} = action.payload;
      if (action.payload.action === 'add' && outsourced === 'S') {
        const baseUrl = process.env.REP_API_URL;
        const fileUrl = `${baseUrl}/quality-assurance/orders/${order}/tasks/${serviceCode3}/document`;
        downloadFile(fileUrl);
      }

      return ({
        ...state,
        'savingItem': false,
        'values': {
          ...state.values,
          'tasks': saveOrDeleteItem(state.values.tasks, action.payload),
        },
      });
    },
    [ActionTypes.MANAGED_TASK_IN_QUALITY_ASSURANCE_ORDER_FAILED]: (state, action) => ({
      ...state,
      'savingItem': false,
    }),
    [ActionTypes.ADD_OUTSOURCED_INFO]: (state, action) => ({
      ...state,
      'values': {
        ...state.values,
        'tasks': addOutsourcedInfo(state.values.tasks, action.payload),
      },
    }),
  })(state, action);
};
